import { supplierRoleForGrant } from "../../shared/rbac/crossResourceAvailability";
import {
  defaultCreatorGrants,
  type GrantEntry,
  grantHasExactPermission,
  type RoleOption,
} from "../../shared/rbac/roles";
import {
  roleDefaultsForSpace,
  type SpaceGrant,
  type SpacePrincipalRoles,
  type SupplierPrincipal,
  strongestSpaceGrants,
} from "./spaceSupplierGrants";

// The connector roles the creator themselves should hold: always editor (edit +
// use), plus sharer (manage access) only when they can share tenant-wide. A
// creator without tenant:connected_app_share can configure the connector but not
// administer who else can reach it — that stays with tenant admins/sharers.
function creatorGrants(userId: string, creatorCanShare: boolean): GrantEntry[] {
  const grants = defaultCreatorGrants("connected_app", { userId });
  return creatorCanShare
    ? grants
    : grants.filter((g) => g.builtinRoleId !== "connected_app_sharer");
}

// Builds the connected-app grants a space's members should hold so the space can
// use the app: editors get full app access (edit + share + use), managers administer
// the space so they also manage the app (read + edit + share), and viewers only
// read. A principal with several space grants gets the roles for its strongest tier.
export function connectorGrantsForSpace(
  spaceGrants: readonly SpaceGrant[],
  currentUser?: { id: string; groupIds: ReadonlySet<string> }
): GrantEntry[] {
  const grants: GrantEntry[] = [];
  for (const { principal, grants: principalGrants } of strongestSpaceGrants(
    spaceGrants,
    "space_uses_connected_apps"
  ))
    grants.push(...appRoleGrants(principal, principalGrants));

  if (grants.length === 0 || !currentUser) return grants;

  const spaceGroupIds = spaceGrants
    .map((g) => g.userGroupId)
    .filter((id): id is string => id != null);
  if (spaceGroupIds.some((id) => currentUser.groupIds.has(id))) return grants;

  return [...grants, ...creatorGrants(currentUser.id, true)];
}

// Maps a space's app grants to the RoleOptions each principal should be pre-staged
// with when the space is added to a connector.
export function connectorRoleDefaultsForSpace(
  spaceGrants: readonly SpaceGrant[],
  roleOptions: readonly RoleOption[]
): SpacePrincipalRoles {
  return roleDefaultsForSpace(
    connectorGrantsForSpace(spaceGrants),
    roleOptions
  );
}

// The connector grants to pre-populate when creating an app. When a space is in
// context the grants mirror the space's access so the space — not just the
// creator — can use the new connector. A creator without
// tenant:connected_app_share cannot grant anyone but themselves, so they get
// creator-only grants regardless of space context (the mutation rejects
// anything else). Returns undefined until the space-grants query has loaded:
// an unloaded query reads as empty, so without this guard we'd silently fall
// through to creator-only grants and create a connector the rest of the space
// can't reach.
export function defaultConnectorGrants({
  userId,
  spaceId,
  spaceGrants,
  spaceGrantsLoaded,
  userGroupIds,
  creatorCanShare,
}: {
  userId: string;
  spaceId: string | undefined;
  spaceGrants: readonly SpaceGrant[];
  spaceGrantsLoaded: boolean;
  userGroupIds: ReadonlySet<string>;
  creatorCanShare: boolean;
}): GrantEntry[] | undefined {
  if (!creatorCanShare) return creatorGrants(userId, false);
  if (spaceId) {
    if (!spaceGrantsLoaded) return undefined;
    const grants = connectorGrantsForSpace(spaceGrants, {
      id: userId,
      groupIds: userGroupIds,
    });
    if (grants.length > 0) return grants;
  }
  return creatorGrants(userId, true);
}

function appRoleGrants(
  principal: SupplierPrincipal,
  spaceGrants: readonly SpaceGrant[]
): GrantEntry[] {
  // Service accounts get a narrower default role than users to avoid granting
  // unnecessary permissions.
  if ("serviceAccountId" in principal)
    return [{ ...principal, builtinRoleId: "connected_app_user" }];
  // All grants share the strongest tier, so any one of them maps to the same
  // supplier role; only the space:manage check must look across all of them.
  const role = supplierRoleForGrant(
    "space_uses_connected_apps",
    spaceGrants[0]!
  );
  if (role === "connected_app_viewer") {
    if (spaceGrants.some((g) => grantHasExactPermission(g, "space:manage"))) {
      return [
        { ...principal, builtinRoleId: "connected_app_viewer" },
        { ...principal, builtinRoleId: "connected_app_editor" },
        { ...principal, builtinRoleId: "connected_app_sharer" },
      ];
    }
    return [{ ...principal, builtinRoleId: "connected_app_viewer" }];
  }
  return defaultCreatorGrants("connected_app", principal);
}
